import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/errors";

/**
 * Central query client.
 *
 * Cache model (PRD §11): resource caches are keyed by full resource name; list
 * caches carry their filter/state/orderBy/pageToken dimensions in the key.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        // Don't retry auth/permission/not-found/client errors; retry transient ones.
        if (error instanceof ApiError) {
          if (error.isNetworkError) return failureCount < 2;
          if (error.httpStatus >= 400 && error.httpStatus < 500 && !error.isRateLimited) return false;
          if (error.isRateLimited) return failureCount < 2;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
});

// ---------------------------------------------------------------------------
// Query keys. Namespaced and stable; lists embed their full request shape.
// ---------------------------------------------------------------------------

export const qk = {
  memo: (name: string) => ["memo", name] as const,
  memoAgentState: (ownerName: string, name: string) =>
    ["memo", ownerName, name, "agent-state"] as const,
  memoComments: (name: string) => ["memo", name, "comments"] as const,
  memoReactions: (name: string) => ["memo", name, "reactions"] as const,
  memoRelations: (name: string) => ["memo", name, "relations"] as const,
  memoShares: (name: string) => ["memo", name, "shares"] as const,
  memoByShare: (token: string) => ["share", token] as const,
  memoList: (params: Record<string, unknown>) => ["memos", "list", params] as const,
  memoAgentBatchPrefix: ["agent", "memo-states"] as const,
  memoAgentBatch: (ownerName: string, names: string[]) =>
    ["agent", "memo-states", ownerName, names] as const,
  user: (name: string) => ["user", name] as const,
  userByUsername: (username: string) => ["user", "by-username", username] as const,
  userStats: (name: string) => ["user", name, "stats"] as const,
  userSettings: (name: string) => ["user", name, "settings"] as const,
  userSetting: (name: string, setting: string) => ["user", name, "settings", setting] as const,
  notifications: (name: string, view: string) => ["user", name, "notifications", view] as const,
  shortcuts: (name: string) => ["user", name, "shortcuts"] as const,
  pats: (name: string) => ["user", name, "pats"] as const,
  webhooks: (name: string) => ["user", name, "webhooks"] as const,
  linkedIdentities: (name: string) => ["user", name, "linked-identities"] as const,
  attachments: (params: Record<string, unknown>) => ["attachments", "list", params] as const,
  users: (params?: Record<string, unknown>) => ["users", "list", params ?? {}] as const,
  identityProviders: () => ["identity-providers"] as const,
  instanceStats: () => ["instance", "stats"] as const,
  linkMetadata: (url: string) => ["link-metadata", url] as const,
};

/** Invalidate every cache belonging to the signed-in user (on account switch). */
export function clearUserScopedCaches(): void {
  queryClient.removeQueries({ predicate: () => true });
}
